'use client';

import { useCommendaOSRead } from '@commenda/api/commenda-os-dsl';
import { LoadingIndicator, SingleFileUploader } from '@commenda/kakashi';
import type { FileBasic } from '@commenda/types/src/file/types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DSLContextMarker } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { isRuleActive } from '../../utils/validation';
import type { DataSourceConfig } from '@commenda/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

interface DirectorDocumentDSLNode {
  id: number | null;
  documentKind: string;
  fileName: string | null;
  fileUrl: string | null;
  documentType: string | null;
  fileNumber: string | null;
  fileIssuedInJurisdiction: string | null;
  fileExpirationDate: string | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
}

const RELATIONSHIP_DOCUMENT_KINDS = new Set([
  'shareCertificate',
  'boardResolution',
  'sharePurchaseAndSaleAgreement',
]);

const DOCUMENT_KIND_LABELS: Record<string, string> = {
  governmentId: 'Government ID',
  panCard: 'PAN Card',
  aadharCard: 'Aadhar Card',
  addressProof: 'Address Proof',
  dscProof: 'DSC Proof',
  bankTransferProof: 'Bank Transfer Proof',
  shareCertificate: 'Share Certificate',
  boardResolution: 'Board Resolution',
  sharePurchaseAndSaleAgreement: 'Share Purchase & Sale Agreement',
};

interface DirectorDocumentComponentProps {
  fieldId: string;
  label?: string;
  guidance?: { text?: string };
  dataSource?: DataSourceConfig;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  props?: {
    documentKind?: string | null;
    mode?: 'read' | 'read-write';
    disabled?: boolean;
  };
}

export const DirectorDocumentComponent = ({
  fieldId,
  label: labelOverride,
  guidance,
  dataSource,
  validation,
  props = {},
}: DirectorDocumentComponentProps) => {
  const { formData, updateField, isFieldVisible, isFieldDisabled, dslContext } =
    useContext(FormContext);
  const {
    documentKind,
    mode = 'read-write',
    disabled: propsDisabled = false,
  } = props;

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const contextBindings = dataSource?.commendaOS?.contextBindings;
  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;
  const VALID_CONTEXT_KEYS = ['companyId', 'corporationId', 'directorId'];

  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };
    if (contextBindings) {
      for (const binding of contextBindings) {
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) continue;
        if (!VALID_CONTEXT_KEYS.includes(binding.contextKey)) continue;
        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }
    const filteredCtx: typeof ctx = {};
    for (const key of Object.keys(ctx)) {
      if (VALID_CONTEXT_KEYS.includes(key)) filteredCtx[key] = ctx[key];
    }
    return filteredCtx;
  }, [dslContext, formData, contextBindings]);

  const isRelationshipDoc = documentKind
    ? RELATIONSHIP_DOCUMENT_KINDS.has(documentKind)
    : false;

  const hasMissingBoundContext = useMemo(() => {
    if (!contextBindings?.length) return false;
    const validBindings = contextBindings.filter((b) =>
      VALID_CONTEXT_KEYS.includes(b.contextKey)
    );
    if (!validBindings.length) return false;
    const missing = validBindings.some((binding) => {
      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        return dslContext?.[binding.contextKey] == null;
      }
      const val = formData[binding.sourceFieldId];
      return val == null || val === '';
    });
    if (missing) return true;
    if (isRelationshipDoc && !effectiveContext?.corporationId) return true;
    return false;
  }, [contextBindings, formData, dslContext, isRelationshipDoc, effectiveContext?.corporationId]);

  const readPath = useMemo(() => {
    if (!documentKind || !effectiveContext?.directorId) return null;
    if (isRelationshipDoc) {
      if (!effectiveContext?.corporationId) return null;
      return `keyPersonRelationship.documents[documentType=${documentKind}]`;
    }
    return `keyPerson.directorDocuments[documentKind=${documentKind}]`;
  }, [documentKind, effectiveContext?.directorId, effectiveContext?.corporationId, isRelationshipDoc]);

  const { data: document, isLoading } =
    useCommendaOSRead<DirectorDocumentDSLNode>(
      readPath,
      effectiveContext as { companyId: number } | undefined,
      { skip: !readPath || hasMissingBoundContext }
    );

  const [file, setFileInternal] = useState<FileBasic | null>(null);
  const hasPrePopulated = useRef(false);

  const setFile = useCallback(
    (newFile: FileBasic | null | ((prev: FileBasic | null) => FileBasic | null)) => {
      setFileInternal((prev) => {
        const resolved = typeof newFile === 'function' ? newFile(prev) : newFile;
        if (!resolved) {
          updateField(fieldId, null);
        }
        return resolved;
      });
    },
    [fieldId, updateField]
  );

  useEffect(() => {
    if (document?.fileUrl && document.fileName) {
      setFileInternal({
        fileName: document.fileName,
        fileUrl: document.fileUrl,
        documentType: document.documentType as FileBasic['documentType'],
      });
      if (!hasPrePopulated.current) {
        hasPrePopulated.current = true;
        updateField(fieldId, {
          fileUrl: document.fileUrl,
          fileName: document.fileName,
          documentKind,
          ...(isRelationshipDoc ? { __isRelationshipDoc: true } : {}),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.fileUrl, document?.fileName, document?.documentType]);

  const handleFileChange = (uploadedFile: FileBasic) => {
    if (uploadedFile?.fileUrl && uploadedFile?.fileName) {
      updateField(fieldId, {
        fileUrl: uploadedFile.fileUrl,
        fileName: uploadedFile.fileName,
        documentKind,
        ...(isRelationshipDoc ? { __isRelationshipDoc: true } : {}),
      });
    }
  };

  const hasCustomLabel = labelOverride && labelOverride !== 'Director Document';
  const kindLabel = documentKind ? DOCUMENT_KIND_LABELS[documentKind] : null;
  const title =
    (hasCustomLabel ? labelOverride : null) || kindLabel || labelOverride || 'Director Document';
  const isReadOnly = mode === 'read';
  const isDisabled =
    propsDisabled ||
    isFieldDisabled(fieldId) ||
    isReadOnly ||
    hasMissingBoundContext;

  if (isLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={title}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  if (!documentKind) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <div className="p-4 border border-dashed rounded-md bg-bg-secondary">
          <p className="text-sm text-text-tertiary text-center">
            Configure document type in the property editor
          </p>
        </div>
      </div>
    );
  }

  if (isReadOnly) {
    const displayFile =
      (formData[fieldId] as
        | { fileUrl: string; fileName: string }
        | undefined) ||
      (document?.fileUrl
        ? { fileUrl: document.fileUrl, fileName: document.fileName }
        : null);

    return (
      <div className="mb-4" data-field-id={fieldId}>
        <LabelWithGuidanceTooltip
          label={title}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
        {displayFile?.fileUrl ? (
          <div className="flex items-center gap-2 p-3 border rounded-md bg-bg-secondary">
            <span className="text-sm text-text-primary flex-1 truncate">
              {displayFile.fileName}
            </span>
            <a
              href={displayFile.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-text-link hover:underline shrink-0"
            >
              View
            </a>
          </div>
        ) : (
          <div className="p-3 border border-dashed rounded-md">
            <p className="text-sm text-text-tertiary">Not uploaded</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      <LabelWithGuidanceTooltip
        label={title}
        guidance={guidance?.text}
        isRequired={isRequired}
      />
      <SingleFileUploader
        file={file as FileBasic}
        setFile={setFile}
        companyId={(effectiveContext.companyId ?? dslContext?.companyId) as number | undefined}
        onFileChange={handleFileChange}
        disabled={isDisabled}
      />
    </div>
  );
};
